import React from "react";
import { motion } from "framer-motion";
import { Award, ShieldCheck, Languages } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_client-attract-7/artifacts/vx6o775k_IMG_0547.jpeg";

export default function About() {
  const { t } = useT();
  const PILLARS = [
    { icon: Award, title: t("about.pillar1.title"), text: t("about.pillar1.text") },
    { icon: ShieldCheck, title: t("about.pillar2.title"), text: t("about.pillar2.text") },
    { icon: Languages, title: t("about.pillar3.title"), text: t("about.pillar3.text") },
  ];
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 sm:py-32 bg-[#EDEDEE]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#F8F7F3]">
              <img
                src={FOUNDER_IMG}
                alt="Founder of Soarlife Consulting"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3F5043]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-[#F8F7F3]">
                <div className="text-xs uppercase tracking-[0.25em] font-semibold opacity-85 mb-1">
                  {t("about.role")}
                </div>
                <div className="font-serif italic text-2xl">Soarlife Consulting</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-4">
              {t("about.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.05] text-[#3F5043]">
              {t("about.title.a")} <br />
              {t("about.title.b")} <em className="text-[#875F45]">{t("about.title.em")}</em>
            </h2>
            <div className="mt-7 space-y-5 text-base sm:text-lg leading-relaxed text-[#3F5043]/85 max-w-2xl">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
              <p className="font-serif italic text-2xl text-[#875F45] leading-snug pt-2">
                {t("about.quote1")}
              </p>
              <p className="font-serif italic text-xl text-[#3F5043]">
                {t("about.quote2")}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    className="rounded-2xl bg-[#F8F7F3] border border-[#BCB3A6]/40 p-6"
                  >
                    <Icon
                      size={22}
                      strokeWidth={1.4}
                      className="text-[#875F45] mb-4"
                    />
                    <div className="font-serif text-xl text-[#3F5043] mb-1.5">
                      {p.title}
                    </div>
                    <p className="text-sm text-[#3F5043]/70 leading-relaxed">
                      {p.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
